import { Center, Text3D } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { WorkInstructionWithUUIDs } from '../model-space/model-space';
import { WorkInstruction } from '../../types/api/work-instruction/get-work-instructions-success-response';
import { getFontUrl } from '../../../../utils/get-font-url/get-font-url';

type POIProps = {
    id?: string | number;
    POIUUID: string;
    POILineEndpointUUID: string;
    workInstruction: WorkInstruction | WorkInstructionWithUUIDs;
    onPOIClick?: () => void;
    onPOILineEndpointClick?: () => void;
    onPointerMissed?: () => void;
    numberOfAllPOI?: number;
};

export const POI = ({
    id,
    POIUUID,
    POILineEndpointUUID,
    workInstruction,
    onPOIClick,
    onPOILineEndpointClick,
    onPointerMissed,
    numberOfAllPOI
}: POIProps) => {
    const groupRef = useRef<THREE.Group<THREE.Object3DEventMap>>();
    const { camera } = useThree();
    const geometryRef = useRef<THREE.BufferGeometry<THREE.NormalBufferAttributes>>();

    useFrame(() => {
        if (groupRef.current) {
            groupRef.current.lookAt(camera.position);
        }
    });

    useEffect(() => {
        if (geometryRef.current) {
            geometryRef.current.setFromPoints([
                new THREE.Vector3(workInstruction.POIXPosition, workInstruction.POIYPosition - 0.46, workInstruction.POIZPosition), // div elem alsó-középső pontja
                new THREE.Vector3(workInstruction.POILineEndpointXPosition, workInstruction.POILineEndpointYPosition, workInstruction.POILineEndpointZPosition)
            ]);
        }
    }, [
        workInstruction.POILineEndpointXPosition,
        workInstruction.POILineEndpointYPosition,
        workInstruction.POILineEndpointZPosition,
        workInstruction.POIXPosition,
        workInstruction.POIYPosition,
        workInstruction.POIZPosition
    ]);

    const workInstructionTitleWithLineBreaks = useMemo(() => {
        let _workInstructionTitleWithLineBreaks = '';

        if (workInstruction.title) {
            const lengthOfOneLine = 18;
            _workInstructionTitleWithLineBreaks = workInstruction.title.substring(0, lengthOfOneLine);

            let currentLength = lengthOfOneLine;
            while (currentLength < workInstruction.title.length) {
                _workInstructionTitleWithLineBreaks = _workInstructionTitleWithLineBreaks + '\n' + workInstruction.title.substring(currentLength, currentLength + lengthOfOneLine);
                currentLength+= lengthOfOneLine;
            }
        }
        
        return _workInstructionTitleWithLineBreaks;
    }, [workInstruction.title]);

    const shape = useMemo(() => {
        const width = 2.4;
        const height = 1.2;
        const radius = 0.3;

        const shape = new THREE.Shape()
        const x = -width / 2
        const y = -height / 2
        shape.moveTo(x + radius, y)
        shape.lineTo(x + width - radius, y)
        shape.quadraticCurveTo(x + width, y, x + width, y + radius)
        shape.lineTo(x + width, y + height - radius)
        shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height)
        shape.lineTo(x + radius, y + height)
        shape.quadraticCurveTo(x, y + height, x, y + height - radius)
        shape.lineTo(x, y + radius)
        shape.quadraticCurveTo(x, y, x + radius, y)
        return shape;
    }, []);

    const roundedGeometry = useMemo(() => new THREE.ShapeGeometry(shape), [shape]);
    
    return (
        <>
            <group
                ref={groupRef as React.Ref<THREE.Group<THREE.Object3DEventMap>>}
                uuid={POIUUID}
                position={[workInstruction.POIXPosition, workInstruction.POIYPosition, workInstruction.POIZPosition]}
                onPointerMissed={onPointerMissed}
                userData={{ isPOI: true }}
                onClick={onPOIClick}
            >
                <mesh geometry={roundedGeometry}>
                    <meshStandardMaterial color='black' />
                </mesh>
                <Center top position={[0, 0.15, 0]}>
                    <Text3D
                        font={getFontUrl('/bai-jamjuree/BaiJamjuree_Bold.json')}
                        size={0.18}
                    >
                        { workInstruction.order }. lépés
                        {
                            numberOfAllPOI !== undefined && ` (${ workInstruction.order }/${ numberOfAllPOI })`
                        }
                        <meshStandardMaterial color='white' />
                    </Text3D>
                </Center>
                <Center position={[0, -0.16, 0]}>
                    <Text3D
                        font={getFontUrl('/bai-jamjuree/BaiJamjuree_Regular.json')}
                        size={0.13}
                        lineHeight={0.5}
                    >
                        { workInstructionTitleWithLineBreaks }
                        <meshStandardMaterial color='white' />
                    </Text3D>
                </Center>
                {/*<Html
                    occlude={htmlOcclude}
                    transform
                    distanceFactor={10} // 100px === 10 világegység
                >
                    <div
                        style={{
                            background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0.75)), linear-gradient(103.07deg, rgba(104, 52, 154, 0.8) 4.77%, rgba(47, 110, 186, 0.8) 47.37%, rgba(139, 104, 221, 0.8) 94.9%)',
                            padding: '5px',
                            color: 'white',
                            userSelect: 'none',
                            height: '38px'
                        }}
                        onClick={
                            () => {
                                // késleltetés, mert az onClick az onPointerMissed előtt fut le,
                                // így elérjük, hogy utána fusson le, és a megfelelő POI-t jelöli ki a TransformControls-nak
                                if (onPOIClick) setTimeout(onPOIClick, 50);
                            }
                        }
                    >
                        <p style={{ fontSize: '7px', textAlign: 'center', margin: '0', padding: '0', fontWeight: 'bold' }}>{ workInstruction.order }. lépés</p>
                        <p style={{ fontSize: '6px', textAlign: 'center', margin: '0', padding: '0' }}>{ workInstruction.title }</p>
                        <p style={{ fontSize: '5px', textAlign: 'center', margin: '0', padding: '0' }}>{ workInstruction.description }</p>
                    </div>
                </Html>*/}
            </group>

            <line>
                <bufferGeometry ref={geometryRef} />
                <lineBasicMaterial color='red' />
            </line>
            <mesh
                position={[workInstruction.POILineEndpointXPosition, workInstruction.POILineEndpointYPosition, workInstruction.POILineEndpointZPosition]}
                uuid={POILineEndpointUUID}
                userData={{ isPOILineEndpoint: true }}
                onClick={() => { if (onPOILineEndpointClick) setTimeout(onPOILineEndpointClick, 0); }}
            >
                <sphereGeometry args={[0.04]} />
                <meshBasicMaterial color='red' />
            </mesh>

        </>
    );
};